"use client";

import { Plus } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";

const ITEMS = [
  {
    q: "What's the difference between Lifetime and the monthly plans?",
    a: "Lifetime is a one-time £67 payment that gives you everything in Growth — 10 live funnels, all features, and every future update — forever. Monthly plans (Starter at £14.99, Growth at £34.99) bill recurring. Lifetime pays for itself in under two months versus Growth.",
  },
  {
    q: "Do I really get future updates for free?",
    a: "Yes. Lifetime buyers get every feature we ship, indefinitely. We've shipped block-editor variants, design templates, lead capture, and analytics so far — all included.",
  },
  {
    q: "Can I cancel a monthly plan anytime?",
    a: "Yes. Monthly plans bill until you cancel, and you keep access through the end of the period you've already paid for. No retention calls, no hoops.",
  },
  {
    q: "What happens after I publish a funnel?",
    a: "Your funnel goes live at your-slug.funnlr.co with HTTPS, fast hosting, and a real CSP header. Leads flow into your dashboard in real time, ready to export or wire into your tools.",
  },
  {
    q: "Can I use a custom domain?",
    a: "Custom domains are on the roadmap. For now, every funnel ships on a Funnlr subdomain — fast, secure, and indexable.",
  },
  {
    q: "How does the refund work?",
    a: "30 days, no questions asked. If Funnlr isn't pulling its weight, email us and we refund. We'd rather have a happy non-customer than a frustrated one.",
  },
];

export function Faq() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="bg-slate-50/70 px-6 py-20 sm:py-28">
      <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.9fr_1.6fr]">
        <div>
          <p
            data-reveal
            className="text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-600"
          >
            FAQ
          </p>
          <h2
            data-reveal
            className="mt-4 font-heading text-[32px] font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-[40px]"
          >
            The short answers.
          </h2>
          <p
            data-reveal
            className="mt-4 text-[14px] leading-relaxed text-slate-500"
          >
            Anything we missed? Email{" "}
            <a
              href="mailto:hello@funnlr.co"
              className="font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-700"
            >
              hello@funnlr.co
            </a>{" "}
            — a real human writes back within a day.
          </p>
        </div>

        <div className="space-y-2" data-reveal-stagger>
          {ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={item.q}
                className={cn(
                  "rounded-xl border bg-white transition-colors",
                  isOpen
                    ? "border-slate-300 shadow-sm"
                    : "border-slate-200/80",
                )}
              >
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-6 px-5 py-4 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-heading text-[15px] font-semibold tracking-tight text-slate-900">
                    {item.q}
                  </span>
                  <Plus
                    className={cn(
                      "h-4 w-4 shrink-0 text-slate-400 transition-transform",
                      isOpen && "rotate-45 text-slate-700",
                    )}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-[14px] leading-relaxed text-slate-600">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
